import React from 'react';
import { ArrowRight, Briefcase, Compass, Layers3, Link2, Sparkles } from 'lucide-react';

const SummaryCard = ({ label, value, helper, testId }) => (
  <div className="rounded-[24px] border border-border-subtle bg-card/40 p-5" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
    <p className="mt-3 text-3xl font-bold text-text-heading">{value}</p>
    <p className="mt-2 text-xs text-text-muted">{helper}</p>
  </div>
);

const statusStyles = {
  listo: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/20',
  recomendado: 'bg-sky-500/15 text-sky-300 border-sky-400/20',
  estable: 'bg-card text-text-muted border-border-subtle',
  bloqueado: 'bg-amber-500/15 text-amber-300 border-amber-400/20',
  queued: 'bg-amber-500/15 text-amber-300 border-amber-400/20',
  running_agents: 'bg-sky-500/15 text-sky-300 border-sky-400/20',
  drafting_report: 'bg-sky-500/15 text-sky-300 border-sky-400/20',
  ready: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/20',
  despachada: 'bg-sky-500/15 text-sky-300 border-sky-400/20',
  pendiente: 'bg-amber-500/15 text-amber-300 border-amber-400/20',
  building: 'bg-sky-500/15 text-sky-300 border-sky-400/20',
  planned: 'bg-card text-text-muted border-border-subtle',
};

export default function ExecutiveFlowHub({ data, onNavigate }) {
  if (!data) return null;

  return (
    <div className="glass-card p-6 space-y-6" data-testid="executive-flow-hub">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Cabina guiada</p>
          <h3 className="mt-2 text-2xl font-semibold text-text-heading">Qué hacer ahora, en qué orden y por qué</h3>
          <p className="mt-2 text-sm text-text-body">Este bloque convierte el admin en un recorrido intuitivo: primero entiendes el contexto, luego despachas, después activas capacidades y finalmente conectas los canales reales.</p>
        </div>
        <div className="rounded-[24px] border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-primary" data-testid="executive-flow-banner">
          Neuralia ya te dice el siguiente paso sin perderte entre pestañas.
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard label="Órdenes abiertas" value={data.daily_summary.open_ceo_orders} helper="Prioridades del CEO activas" testId="executive-flow-metric-orders" />
        <SummaryCard label="Workflows vivos" value={data.daily_summary.active_workflows} helper="Briefings transformándose en ejecución" testId="executive-flow-metric-workflows" />
        <SummaryCard label="Conexiones pendientes" value={data.daily_summary.pending_integrations} helper="Bloqueos para pasar a canales reales" testId="executive-flow-metric-integrations" />
        <SummaryCard label="Skills por empujar" value={data.daily_summary.strategic_skills_to_push} helper="Capacidades estratégicas aún no activas" testId="executive-flow-metric-skills" />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-4" data-testid="executive-flow-guided-steps">
          {data.guided_steps.map((step) => (
            <div key={step.id} className="rounded-[24px] border border-border-subtle bg-background/55 p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="max-w-2xl">
                  <div className={`inline-flex rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.18em] ${statusStyles[step.status] || 'bg-card text-text-muted border-border-subtle'}`} data-testid={`executive-flow-step-status-${step.id}`}>
                    {step.status}
                  </div>
                  <h4 className="mt-3 text-lg font-semibold text-text-heading">{step.title}</h4>
                  <p className="mt-2 text-sm text-text-body">{step.description}</p>
                </div>
                <button className="btn-secondary text-sm" onClick={() => onNavigate(step.target_tab)} data-testid={`executive-flow-step-button-${step.id}`}>
                  <ArrowRight className="w-4 h-4" /> {step.cta_label}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <div className="rounded-[24px] border border-border-subtle bg-card/40 p-5" data-testid="executive-flow-inbox">
            <div className="flex items-center gap-2 text-text-heading">
              <Briefcase className="w-5 h-5 text-primary" />
              <h4 className="text-lg font-semibold">Bandeja única de acciones</h4>
            </div>
            <div className="mt-4 space-y-3">
              {data.action_inbox.map((item) => (
                <div key={`${item.kind}-${item.id}`} className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`executive-flow-inbox-item-${item.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-text-heading">{item.title}</p>
                      <p className="mt-1 text-sm text-text-body">{item.description}</p>
                    </div>
                    <span className={`rounded-full border px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${statusStyles[item.status] || 'bg-card text-text-muted border-border-subtle'}`} data-testid={`executive-flow-inbox-status-${item.id}`}>
                      {item.status}
                    </span>
                  </div>
                  <button className="mt-3 text-sm font-medium text-primary hover:text-primary/80" onClick={() => onNavigate(item.target_tab)} data-testid={`executive-flow-inbox-button-${item.id}`}>
                    Abrir {item.target_tab.replace('_', ' ')}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[24px] border border-border-subtle bg-card/40 p-5" data-testid="executive-flow-suggestions">
            <div className="flex items-center gap-2 text-text-heading">
              <Sparkles className="w-5 h-5 text-primary" />
              <h4 className="text-lg font-semibold">Automatizaciones sugeridas</h4>
            </div>
            <div className="mt-4 space-y-3 text-sm text-text-body">
              {data.automation_suggestions.map((item, index) => (
                <div key={item} className="flex items-start gap-3" data-testid={`executive-flow-suggestion-${index}`}>
                  {index % 3 === 0 ? <Compass className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" /> : index % 3 === 1 ? <Layers3 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" /> : <Link2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />}
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}